//#region IMPORT
import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Dialog, Button } from '../../../../Components/MUIs';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  VerifiedUser as VerifiedUserIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Schedule as ScheduleIcon,
  AccountCircle as AccountCircleIcon,
  Draw as DrawIcon,
} from '@mui/icons-material';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
//#endregion

const GREY_BG = '#f4f6f8';
const BORDER_DASH = '1px dashed #c4cdd5';

function FormatDepFromUserInfo(userInfo) {
  if (!userInfo) return '';
  const parts = [
    userInfo.depNm1,
    userInfo.depNm2,
    userInfo.depNm3,
    userInfo.depNm4,
  ].filter((p) => p && String(p).trim());
  return parts.join(' ') || '';
}

function StatusBadge({ done, labelDone = '서명완료', labelWait = '대기중' }) {
  if (done) {
    return (
      <Chip
        size="small"
        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
        label={labelDone}
        sx={{
          bgcolor: '#e8f5e9',
          color: '#2e7d32',
          fontWeight: 600,
          '& .MuiChip-icon': { color: '#2e7d32' },
        }}
      />
    );
  }
  return (
    <Chip
      size="small"
      icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
      label={labelWait}
      sx={{
        bgcolor: '#eceff1',
        color: '#546e7a',
        fontWeight: 600,
        '& .MuiChip-icon': { color: '#78909c' },
      }}
    />
  );
}

/**
 * 결재 상신 팝업 — 중간 결재자 최대 2명, 최종 결재자 1명 선택 UI
 * 상신 클릭 후 신청자 서명 단계에서 서명 확인 시 onSubmit(payload) 호출
 */
function TRA_CUSTOM_02_ApprovalModal({
  open,
  onClose,
  userInfo,
  intermediateApprovers = [],
  finalApprover = null,
  approvalSignStatus = null,
  onRemoveIntermediate,
  onOpenIntermediatePicker,
  onOpenFinalPicker,
  onSubmit,
  validateBeforeSignStep,
}) {
  const [step, setStep] = useState('form');
  const [signError, setSignError] = useState(false);
  const signatureRef = useRef(null);

  const drafterName = userInfo?.userNm || userInfo?.name || '';
  const drafterDep = FormatDepFromUserInfo(userInfo);
  const drafterTitle =
    userInfo?.position || userInfo?.rank || userInfo?.title || '';

  const applicantSigned = Boolean(approvalSignStatus?.applicantSigned);
  const finalSigned = Boolean(approvalSignStatus?.finalSigned);
  const midSignedById = approvalSignStatus?.midSignedById || {};
  const applicantSignPath = approvalSignStatus?.applicantSignPath || '';

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setSignError(false);
  }, [open]);

  useEffect(() => {
    if (open && step === 'sign' && signatureRef.current) {
      signatureRef.current.clear();
      setSignError(false);
    }
  }, [open, step]);

  const HandleClose = () => {
    setStep('form');
    setSignError(false);
    onClose?.();
  };

  const HandleClickSubmit = () => {
    if (!validateBeforeSignStep?.()) {
      return;
    }
    setStep('sign');
  };

  const HandleClearSignature = () => {
    signatureRef.current?.clear();
    setSignError(false);
  };

  const HandleConfirmSignature = async () => {
    const sig = signatureRef.current;
    if (!sig || sig.isEmpty()) {
      setSignError(true);
      return;
    }
    setSignError(false);
    const signatureDataUrl = sig.toDataURL('image/png');
    const decodedData = atob(signatureDataUrl.split(',')[1]);
    const byteNumbers = new Array(decodedData.length);
    for (let i = 0; i < decodedData.length; i++) {
      byteNumbers[i] = decodedData.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });
    const file = new File(
      [blob],
      `approval_sign_${Date.now()}.png`,
      { type: 'image/png' }
    );
    const result = await onSubmit?.({
      signatureDataUrl,
      signatureFile: file,
    });

    // 상신 처리 성공 시 결재선 설정 화면으로 복귀
    if (result !== false) {
      setStep('form');
    }
  };

  const dialogTitle =
    step === 'sign' ? '신청자 서명' : '결재 상신';

  return (
    <Dialog
      open={open}
      onClose={HandleClose}
      title={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            pr: 0,
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {dialogTitle}
          </Typography>
          <IconButton aria-label="닫기" size="small" onClick={HandleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      }
      useCloseButton={false}
      useSaveButton={false}
      fullWidth
      maxWidth="md"
      contentSx={{
        pt: 1,
        pb: 2,
        px: 2,
        maxHeight: 'min(88vh, 900px)',
        overflow: 'auto',
      }}
      content={
        <Box>
          {step === 'sign' ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DrawIcon color="primary" />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  아래 영역에{' '}
                  <Typography component="span" color="primary" fontWeight={700}>
                    {drafterName || '신청자'}
                  </Typography>
                  님의 서명을 직접 작성해 주세요.
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                터치펜·마우스로 서명합니다. 잘못 그렸다면 &quot;지우기&quot;로
                비운 뒤 다시 작성할 수 있습니다.
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  p: 1,
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    '& .sigCanvas': { display: 'block', width: '100% !important' },
                  }}
                >
                  <SignatureCanvas
                    ref={signatureRef}
                    penColor="black"
                    minWidth={2}
                    maxWidth={4}
                    throttle={16}
                    canvasProps={{
                      className: 'sigCanvas',
                      style: {
                        height: Commons.IsMobile() ? 200 : 220,
                        width: '100%',
                        borderRadius: 8,
                        backgroundColor: '#E4EFED',
                        border: '1px solid #bdbdbd',
                        touchAction: 'none',
                      },
                    }}
                    onEnd={() => setSignError(false)}
                  />
                  <Typography
                    sx={{
                      fontSize: '2.5rem',
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      color: 'rgba(0, 0, 0, 0.08)',
                      fontWeight: 600,
                      maxWidth: '85%',
                      textAlign: 'center',
                      wordBreak: 'keep-all',
                    }}
                  >
                    {drafterName}
                  </Typography>
                </Box>
              </Box>
              {signError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  서명을 입력한 뒤 확인을 눌러 주세요.
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1.5,
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid #eceff1',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  name="지우기"
                  variant="outlined"
                  onClick={HandleClearSignature}
                  sx={{
                    minWidth: 88,
                    borderColor: '#cfd8dc',
                    color: 'text.primary',
                  }}
                />
                <Button
                  name="이전"
                  variant="outlined"
                  onClick={() => setStep('form')}
                  sx={{
                    minWidth: 88,
                    bgcolor: '#fff',
                    borderColor: '#cfd8dc',
                    color: 'text.primary',
                  }}
                />
                <Button
                  name="확인"
                  variant="contained"
                  onClick={HandleConfirmSignature}
                  sx={{
                    minWidth: 88,
                    bgcolor: '#1565c0',
                    color: '#fff',
                    '&:hover': { bgcolor: '#0d47a1' },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <>
          {/* 결재선 설정 */}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 1.5,
              }}
            >
              <SettingsIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                결재선 설정
              </Typography>
            </Box>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: GREY_BG,
                borderRadius: 2,
                border: '1px solid #e0e6ed',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                {/* 중간 결재자 */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, mb: 1.25, color: 'text.primary' }}
                  >
                    중간 결재자 (최대 2인)
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      alignItems: 'flex-start',
                    }}
                  >
                    {intermediateApprovers.map((p, idx) => (
                      <Paper
                        key={`${p.id}-${idx}`}
                        elevation={0}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.25,
                          py: 0.75,
                          borderRadius: 1.5,
                          border: '1px solid #cfd8dc',
                          bgcolor: '#fff',
                          maxWidth: '100%',
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700 }}
                            noWrap
                          >
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {p.depNm}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          aria-label="제거"
                          onClick={() => onRemoveIntermediate?.(idx)}
                          sx={{ ml: 0.25 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                    {intermediateApprovers.length < 2 && (
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => onOpenIntermediatePicker?.()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onOpenIntermediatePicker?.();
                          }
                        }}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 44,
                          height: 44,
                          border: BORDER_DASH,
                          borderRadius: 1.5,
                          cursor: 'pointer',
                          bgcolor: '#fff',
                          color: 'primary.main',
                          '&:hover': { bgcolor: '#f0f7ff' },
                        }}
                      >
                        <AddIcon />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 최종 결재자 */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderLeft: { sm: '1px solid #dde3ea' },
                    pl: { sm: 2 },
                    pt: { xs: 1, sm: 0 },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, mb: 1.25 }}
                  >
                    최종 결재자
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid #cfd8dc',
                        bgcolor: '#fff',
                        minHeight: 72,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: '#e3f2fd',
                        }}
                      >
                        <AccountCircleIcon
                          sx={{ fontSize: 40, color: '#1565c0' }}
                        />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        {finalApprover ? (
                          <>
                            <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
                              {finalApprover.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {finalApprover.depNm}
                            </Typography>
                          </>
                        ) : (
                          <Typography color="text.secondary" variant="body2">
                            결재자를 선택하세요
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                    <IconButton
                      color="primary"
                      aria-label="최종 결재자 선택"
                      onClick={() => onOpenFinalPicker?.()}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* 서명 현황 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
              <VerifiedUserIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                서명 현황
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                mb: 2,
              }}
            >
              {/* 기안자 */}
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #e0e6ed',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  기안자 (신청자)
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mt: 1,
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {drafterName || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[drafterDep, drafterTitle].filter(Boolean).join(' / ') ||
                        '—'}
                    </Typography>
                  </Box>
                  <StatusBadge done={applicantSigned} />
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    border: BORDER_DASH,
                    borderRadius: 1,
                    textAlign: 'center',
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafafa',
                  }}
                >
                  {applicantSigned && applicantSignPath ? (
                    <img
                      src={
                        Global.file.useUrl +
                        'files/' +
                        Commons.EncodeUrl(applicantSignPath)
                      }
                      alt="신청자 서명"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 46,
                        objectFit: 'contain',
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontFamily:
                          '"Brush Script MT", "Segoe Script", Georgia, serif',
                        fontSize: '1.35rem',
                        color: '#37474f',
                      }}
                    >
                      {drafterName}
                    </Typography>
                  )}
                </Box>
              </Paper>

              {/* 최종 결재자 (현황) */}
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #e0e6ed',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  최종 결재자
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mt: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {finalApprover?.name || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {finalApprover?.depNm || '미지정'}
                    </Typography>
                  </Box>
                  <StatusBadge done={finalSigned} />
                </Box>
              </Paper>
            </Box>

            {/* 검토구간 — 중간 결재자 */}
            <Box
              sx={{
                p: 2,
                border: BORDER_DASH,
                borderRadius: 2,
                bgcolor: '#fafbfc',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
                검토구간
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                {[0, 1].map((slot) => {
                  const p = intermediateApprovers[slot];
                  return (
                    <Paper
                      key={slot}
                      elevation={0}
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid #e0e6ed',
                        bgcolor: '#fff',
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        중간결재자 {slot + 1}
                      </Typography>
                      {p ? (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mt: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                {p.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {p.depNm}
                              </Typography>
                            </Box>
                            <StatusBadge
                              done={Boolean(midSignedById[String(p?.id)])}
                            />
                          </Box>
                        </>
                      ) : (
                        <Box
                          sx={{
                            mt: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'text.secondary',
                          }}
                        >
                          <ScheduleIcon sx={{ fontSize: 18 }} />
                          <Typography variant="body2">미지정</Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* 하단 버튼 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              mt: 3,
              pt: 2,
              borderTop: '1px solid #eceff1',
            }}
          >
            <Button
              name="취소"
              variant="outlined"
              onClick={HandleClose}
              sx={{
                minWidth: 88,
                bgcolor: '#fff',
                borderColor: '#cfd8dc',
                color: 'text.primary',
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#b0bec5' },
              }}
            />
            <Button
              name="상신"
              variant="contained"
              onClick={HandleClickSubmit}
              sx={{
                minWidth: 88,
                bgcolor: '#1565c0',
                color: '#fff',
                '&:hover': { bgcolor: '#0d47a1' },
              }}
            />
          </Box>
            </>
          )}
        </Box>
      }
    />
  );
}

export default TRA_CUSTOM_02_ApprovalModal;
